import React, { useState } from 'react'
import RangeSlider from 'react-bootstrap-range-slider'
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  Row
} from 'react-angle-dashboard-components'
import styled from 'styled-components'
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css'
import './range-styles.css'
import { Contribution } from '../../interfaces/contribution'
import { capitalize } from '@angle/utils'

interface IQuestionOption {
  label: string
  value: any
  text?: string
}

interface IQuestionRangeOption {
  name: string
  range: { from: number; to: number }
  symbol: string
  isDisabled?: boolean
}

export interface IQuestionProps {
  text: string
  subtitle?: JSX.Element
  options?: IQuestionOption[]
  highlight?: boolean
  onChange: (value: any, details?: any) => void
  type: 'choice' | 'select' | 'boolean' | 'range' | 'text'
  placeholder?: string
  defaultValue?: any
  defaultRangeName?: any
  rangeOptions?: IQuestionRangeOption[]
  booleanPlaceholderText?: Partial<{ yes: string; no: string }>
  styles?: Record<string, string>
  controlType?: 'text' | 'number'
  as?: any
  mask?: string
  maskChar?: string
}

export const MAX_ASNWER_WIDTH = 380
export const PRIMARY_COLOR = '#6d39d9'

const Option = styled.button<{ selected?: boolean; block?: boolean }>`
  padding: ${(props) => (props.block ? '1em 0em' : '1em')};
  background: transparent;
  display: ${(props) => (props.block ? 'block' : 'inline-block')};
  border: 2px solid
    ${(props) => (props.selected ? PRIMARY_COLOR : 'var(--gray-400)')};
  color: ${(props) => (props.selected ? PRIMARY_COLOR : 'var(--gray-400)')};
  margin: 1em 1em 0em 0em;
  text-align: center;
  border-radius: 5px;
  min-width: 250px;
  max-width: ${MAX_ASNWER_WIDTH}px;
`

const Text = styled.h1`
  color: black;
  font-family: Sentinel;
`

const ButtonGroupOption = styled(Button)<{ selected?: boolean }>`
  background: ${(props) =>
    props.selected ? PRIMARY_COLOR : 'transparent'} !important;
  border: 2px solid
    ${(props) => (props.selected ? PRIMARY_COLOR : 'var(--gray-400)')} !important;
  color: ${(props) =>
    props.selected ? 'white' : 'var(--gray-400)'} !important;
`

export const Question: React.FC<IQuestionProps> = ({
  text,
  subtitle = undefined,
  highlight = true,
  options = [],
  onChange,
  type = 'boolean',
  placeholder,
  defaultValue = undefined,
  rangeOptions = [],
  booleanPlaceholderText,
  defaultRangeName = null,
  styles = {},
  controlType = 'text',
  as,
  mask,
  maskChar
}) => {
  const [currentValue, setCurrentValue] = useState(defaultValue)
  const [currentText, setCurrentText] = useState<string | undefined>(
    defaultValue ? booleanPlaceholderText?.yes : booleanPlaceholderText?.no
  )
  const [rangeOption, setRangeMode] = useState(
    rangeOptions.find((e) => e.name === defaultRangeName) || rangeOptions[0]
  )

  const handleClick =
    ({
      value,
      text,
      type = 'button',
      name = ''
    }: Omit<IQuestionOption, 'label'> & { type?: string; name?: string }) =>
    () => {
      setCurrentValue(value)
      setCurrentText(text || undefined)
      onChange(value, { type, name })
    }

  return (
    <div className="text-center">
      {highlight ? (
        <Text>
          {text.slice(0, text.length - 1)}
          <span className="text-primary">{text[text.length - 1]}</span>
        </Text>
      ) : (
        <Text>{text}</Text>
      )}
      {subtitle}

      <div style={{ marginTop: '3rem' }}>
        {type === 'text' && (
          <div className="mb-5">
            <Form.Control
              type={controlType}
              aria-label={text}
              className="bg-white mb-4 d-inline"
              size="lg"
              defaultValue={defaultValue}
              placeholder={placeholder}
              style={{
                maxWidth: MAX_ASNWER_WIDTH,
                border: `2px solid ${
                  currentValue ? PRIMARY_COLOR : 'var(--gray-400)'
                }`,
                color: currentValue ? PRIMARY_COLOR : 'var(--gray-400)',
                ...styles
              }}
              onChange={(e) => {
                const value = e.target.value
                handleClick({ value })()
              }}
              as={as}
              mask={mask}
              maskChar={maskChar}
            ></Form.Control>
          </div>
        )}

        {type === 'boolean' && (
          <div className="mb-5">
            <Option
              selected={currentValue}
              role="button"
              onClick={handleClick({
                value: true,
                text: booleanPlaceholderText?.yes
              })}
            >
              Yes
            </Option>

            <Option
              selected={typeof currentValue === 'boolean' && !currentValue}
              role="button"
              onClick={handleClick({
                value: false,
                text: booleanPlaceholderText?.no
              })}
            >
              No
            </Option>
          </div>
        )}

        {type === 'choice' && (
          <ul className="pl-0 m-auto w-50">
            {options.map((op, i) => (
              <Option
                block
                selected={currentValue === op.value}
                as="li"
                role="button"
                className="mx-auto"
                key={op.label}
                onClick={handleClick(op)}
              >
                {op.label}
              </Option>
            ))}
          </ul>
        )}

        {type === 'select' && (
          <Form.Control
            as="select"
            aria-label={text}
            className="bg-white mb-4 d-inline"
            size="lg"
            defaultValue={defaultValue || ''}
            style={{
              maxWidth: MAX_ASNWER_WIDTH,
              border: `2px solid ${
                currentValue ? PRIMARY_COLOR : 'var(--gray-400)'
              }`,
              color: currentValue ? PRIMARY_COLOR : 'var(--gray-400)'
            }}
            onChange={(e) => {
              const option = options.find(
                (f) => String(f.value) === e.target.value
              )
              if (option) handleClick(option)()
            }}
          >
            {placeholder && !defaultValue && !currentValue && (
              <option disabled value="">
                {placeholder}
              </option>
            )}
            {options.map((op, i) => (
              <option
                value={op.value}
                // selected={currentValue === op.value}
                key={op.label}
              >
                {op.label}
              </option>
            ))}
          </Form.Control>
        )}

        {type === 'range' && (
          <div className="text-center">
            <ButtonGroup className="mb-5" aria-label="question-options-buttons">
              {rangeOptions.map((e, i) => (
                <ButtonGroupOption
                  key={i}
                  selected={rangeOption.name === e.name}
                  onClick={() => {
                    handleClick({
                      value: e.name === Contribution.DOLLAR ? undefined : 50,
                      type: 'range',
                      name: e.name
                    })()

                    setRangeMode(e)
                  }}
                  disabled={e.isDisabled}
                >
                  {capitalize(e.name)}
                </ButtonGroupOption>
              ))}
            </ButtonGroup>

            {rangeOption.name === Contribution.DOLLAR ? (
              <div className="d-flex justify-content-center align-items-center">
                <span style={{ fontSize: 22 }} className="mr-2">
                  $
                </span>
                <input
                  placeholder="0"
                  className="form-control w-50"
                  type="number"
                  value={currentValue}
                  onChange={(e) =>
                    handleClick({
                      value: e.target.value,
                      type: 'range',
                      name: rangeOption.name
                    })()
                  }
                />
              </div>
            ) : (
              <div>
                <RangeSlider
                  min={rangeOption.range.from}
                  max={rangeOption.range.to}
                  tooltip="on"
                  tooltipPlacement="bottom"
                  value={currentValue < 100 ? currentValue : 100}
                  variant="primary"
                  tooltipLabel={(currentValue) =>
                    `${currentValue}${rangeOption.symbol}`
                  }
                  onChange={(e) =>
                    handleClick({
                      value: e.target.value,
                      type: 'range',
                      name: rangeOption.name
                    })()
                  }
                />
                <Row>
                  <Col className="text-left">
                    <small>
                      {rangeOption.range.from}
                      {rangeOption.symbol}
                    </small>
                  </Col>
                  <Col className="text-right">
                    <small>
                      {rangeOption.range.to}
                      {rangeOption.symbol}
                    </small>
                  </Col>
                </Row>
              </div>
            )}

            {/* <Form.Control
            type="range"
            min={rangeOption.range.from}
            max={rangeOption.range.to}
            id="question-range"
            name="question-range"
            onChange={(e) =>
              handleClick({
                value: e.target.value,
                type: 'range',
                name: rangeOption.name
              })()
            }
          /> */}
          </div>
        )}
      </div>
      {currentText && <small style={{ color: '#71869d' }}>{currentText}</small>}
    </div>
  )
}

export default Question
